import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiErrorKind, RETRYABLE_ERROR_KINDS } from '@/shared/api/constants';
import { isApiError } from '@/shared/api/errors';
import { store } from '@/app/store/store';
import { toast } from '@/shared/toast/toastSlice';
import i18n from '@/i18n/config';

/** Per-query options read by the global error handler: `useQuery({ ..., meta: { errorMessage } })`. */
export interface ApiQueryMeta extends Record<string, unknown> {
  /** What to tell the user when this query or mutation fails. */
  errorMessage?: string;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: ApiQueryMeta;
    mutationMeta: ApiQueryMeta;
  }
}

/** The one place API failures reach the user. Components only handle errors they can recover from. */
const reportError = (error: unknown, meta: ApiQueryMeta | undefined) => {
  // Cancellation is intentional; auth failures are handled by the session-expired listener.
  if (
    isApiError(error) &&
    (error.kind === ApiErrorKind.Canceled || error.kind === ApiErrorKind.Unauthorized)
  ) {
    return;
  }
  // Raw error messages can be technical (axios / ProblemDetails), so only meta.errorMessage is shown.
  const message = meta?.errorMessage ?? i18n.t('toast.errorGeneric');
  const requestId =
    isApiError(error) && error.kind === ApiErrorKind.Server ? error.requestId : undefined;
  store.dispatch(
    toast.error(
      i18n.t('toast.errorTitle'),
      requestId ? i18n.t('toast.errorReference', { message, requestId }) : message,
    ),
  );
  console.error('[api]', meta?.errorMessage ?? (error instanceof Error ? error.message : error), {
    kind: isApiError(error) ? error.kind : undefined,
    status: isApiError(error) ? error.status : undefined,
    requestId: isApiError(error) ? error.requestId : undefined,
  });
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (error, query) => reportError(error, query.meta) }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => reportError(error, mutation.meta),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Retry only what can succeed a moment later; 4xx and business errors fail fast.
      retry: (failureCount, error) =>
        failureCount < 2 && isApiError(error) && RETRYABLE_ERROR_KINDS.includes(error.kind),
      refetchOnWindowFocus: false,
    },
  },
});
